import { Button, Card, Flex, Radio, Select, Tag, Typography } from "antd";
import CardSectionTitle from "components/CardSectionTitle.component";
import { ReadOnlyWhenRestricted } from "components/capability";
import { useAdminCapabilities } from "hooks/useAdminCapabilities";
import { AdminCapability } from "shared/utils/capabilities";
import { useClassAssignmentPreview } from "components/modals/ClassAssignmentConflictWarningModal";
import { useEffect, useState } from "react";
import { getAllClassTypeByBusinessId } from "hooks/queries/classtype";
import { useSetStaffClassTypes } from "hooks/mutations/staff";
import { thStyle } from "shared/components/TableText";
import { translate } from "shared/languages";
import { ArchivedClassAssignment, StaffClassType } from "shared/types";
import { ClassCategory } from "shared/types/enum";
import { textPx } from "shared/constants/zoom";

type AssignmentMode = "all" | "specific";

interface InstructorClassTypesProps {
  staffId: string;
  classTypes: StaffClassType[];
  // True when this instructor is already saved as "assigned to everything."
  assignedAllClassTypes: boolean;
  // Non-null when a prior real assignment was archived (Kelas yang
  // Ditugaskan was turned off then back on business-wide) and hasn't been
  // restored or superseded by a newer save since — see
  // updateBusinessPolicies.
  archivedClassAssignment: ArchivedClassAssignment | null;
}

// "All" whenever the stored data says so — which, since
// staff.assigned_all_class_types now DEFAULTS to true server-side (a
// never-configured instructor is unrestricted, the same safe default
// working_hours_configured=false already gives Jam Kerja/Cuti), already
// covers a genuinely new instructor too. Trust the flag alone: an instructor
// can be legitimately saved as "specific, assigned to zero classes"
// (assignedAllClassTypes=false, classTypes=[]), and a `classTypes.length ===
// 0` fallback would misread that real state as "all" on the next load.
const initialMode = (
  assignedAllClassTypes: boolean,
): AssignmentMode => (assignedAllClassTypes ? "all" : "specific");

// Private/Open grouping with hardcoded English labels, like ClassTypeBadge
// (class types stay English in both languages, not i18n).
const GROUPS: { label: string; type: ClassCategory }[] = [
  { label: "Open", type: ClassCategory.Open },
  { label: "Private", type: ClassCategory.Private },
];

// When employee_management_enabled is on, only class types assigned here
// appear as selectable instructors on the schedule creation form (see the
// class_type_id filter CreateScheduleDrawer passes to getAllStaffByBusinessId).
//
// The add control and the assigned list are deliberately separate pieces
// (not one packed multi-select): a single-select "search & add" box that
// always resets to empty after a pick, plus the actual assignments rendered
// as their own grouped, spaced-out pill list below. Keeps the add box a
// fixed size regardless of how many classes an instructor ends up with,
// instead of growing into a long wrapped bar of tags.
const InstructorClassTypes = ({
  staffId,
  classTypes,
  assignedAllClassTypes,
  archivedClassAssignment,
}: InstructorClassTypesProps) => {
  const [selected, setSelected] = useState<string[]>(
    classTypes.map((c) => c.id),
  );
  const [mode, setMode] = useState<AssignmentMode>(() => initialMode(assignedAllClassTypes));
  const [dirty, setDirty] = useState(false);
  // Pembatasan Admin: the Manager's operational-settings toggle names
  // instructor availability, so a restricted Admin sees this page read-only.
  // The explicit `disabled={!dirty}` on the save button beats the disabled
  // context, so the restriction is OR'd in there by hand.
  const { isRestricted } = useAdminCapabilities();
  const restricted = isRestricted(AdminCapability.OperationalSettings);
  // Bumped after every add to force the Select to remount, so it always
  // renders empty rather than showing the just-picked class as its value.
  const [addKey, setAddKey] = useState(0);
  const { data: allClassTypes = [] } = getAllClassTypeByBusinessId();
  const { mutate: saveClassTypes, mutateAsync: saveClassTypesAsync, isPending } = useSetStaffClassTypes();
  const { checkAndConfirm, classAssignmentPreviewWarning } = useClassAssignmentPreview();
  const [isCheckingConflicts, setIsCheckingConflicts] = useState(false);
  // Locally dismisses the "Pulihkan Pengaturan Sebelumnya" button the moment
  // it's clicked — see the matching comment in InstructorWorkingHours.tsx.
  const [restoredFromArchive, setRestoredFromArchive] = useState(false);

  useEffect(() => {
    setSelected(classTypes.map((c) => c.id));
    setMode(initialMode(assignedAllClassTypes));
    setDirty(false);
    setRestoredFromArchive(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [staffId]);

  const onSave = async () => {
    const form = {
      staff_id: staffId,
      class_type_ids: mode === "all" ? [] : selected,
      assigned_all_class_types: mode === "all",
    };
    setIsCheckingConflicts(true);
    try {
      await checkAndConfirm(
        form,
        () => {
          saveClassTypes(form, { onSuccess: () => setDirty(false) });
        },
        // "Hapus Kelas & Simpan": awaited so the warning modal closes only
        // once the server has deleted the classes and saved the assignment.
        async () => {
          await saveClassTypesAsync({ ...form, remove_affected_schedules: true });
          setDirty(false);
        },
      );
    } finally {
      setIsCheckingConflicts(false);
    }
  };

  const addClass = (id: string) => {
    setSelected((prev) => [...prev, id]);
    setDirty(true);
    setAddKey((k) => k + 1);
  };

  const removeClass = (id: string) => {
    setSelected((prev) => prev.filter((s) => s !== id));
    setDirty(true);
  };

  const changeMode = (next: AssignmentMode) => {
    setMode(next);
    setDirty(true);
  };

  // Loads the archived list into the form (mode "specific", since an
  // archive only ever exists for an instructor who had a real list, never
  // for "all") — nothing is saved yet. Same reasoning as
  // InstructorWorkingHours.tsx's restoreArchived: the existing Simpan
  // Perubahan flow already runs the class-assignment conflict check before
  // saving, so this needs no separate check of its own.
  const restoreArchived = () => {
    if (!archivedClassAssignment) return;
    setSelected(archivedClassAssignment.class_type_ids);
    setMode("specific");
    setDirty(true);
    setRestoredFromArchive(true);
  };

  // Only offer classes not already assigned, so the add box can't add a
  // duplicate.
  const addOptions = GROUPS.map((g) => ({
    label: g.label,
    options: allClassTypes
      .filter((c) => c.type === g.type && !selected.includes(c.id))
      .map((c) => ({ label: c.name, value: c.id })),
  })).filter((g) => g.options.length > 0);

  const selectedByGroup = GROUPS.map((g) => ({
    label: g.label,
    items: allClassTypes.filter(
      (c) => c.type === g.type && selected.includes(c.id),
    ),
  })).filter((g) => g.items.length > 0);

  return (
    <>
      <ReadOnlyWhenRestricted capability={AdminCapability.OperationalSettings}>
      <Card>
      <Flex vertical gap={18}>
        <Flex justify="space-between" align="flex-start" gap={16}>
          <CardSectionTitle title={translate("tabs.assigned_classes")} />
          <Flex gap={8}>
            {archivedClassAssignment && !restoredFromArchive && (
              <Button onClick={restoreArchived}>
                {translate("class_settings.employee_management.restore_previous")}
              </Button>
            )}
            <Button
              type="primary"
              loading={isPending || isCheckingConflicts}
              disabled={!dirty || restricted}
              onClick={onSave}
            >
              {translate("button.save_changes")}
            </Button>
          </Flex>
        </Flex>

        <Radio.Group
          value={mode}
          onChange={(e) => changeMode(e.target.value as AssignmentMode)}
          options={[
            {
              label: translate("class_settings.employee_management.assignment_mode_all"),
              value: "all",
            },
            {
              label: translate("class_settings.employee_management.assignment_mode_specific"),
              value: "specific",
            },
          ]}
        />

        {mode === "all" ? (
          <Typography.Text type="secondary">
            {translate("class_settings.employee_management.assigned_all_hint")}
          </Typography.Text>
        ) : (
          <>
            <Select<string>
              key={addKey}
              showSearch
              optionFilterProp="label"
              placeholder={translate(
                "class_settings.employee_management.add_class_placeholder",
              )}
              style={{ width: "100%", maxWidth: 400 }}
              onSelect={addClass}
              options={addOptions}
            />

            {selectedByGroup.length === 0 ? (
              <Typography.Text type="secondary">
                {translate("class_settings.employee_management.no_classes")}
              </Typography.Text>
            ) : (
              <Flex vertical gap={16}>
                {selectedByGroup.map((group) => (
                  <Flex key={group.label} vertical gap={8}>
                    <span style={thStyle}>{group.label}</span>
                    <Flex wrap gap={8}>
                      {group.items.map((item) => (
                        <Tag
                          key={item.id}
                          closable
                          onClose={() => removeClass(item.id)}
                          style={{ padding: "4px 10px", fontSize: textPx(13) }}
                        >
                          {item.name}
                        </Tag>
                      ))}
                    </Flex>
                  </Flex>
                ))}
              </Flex>
            )}
          </>
        )}
      </Flex>
      </Card>
      </ReadOnlyWhenRestricted>
      {classAssignmentPreviewWarning}
    </>
  );
};

export default InstructorClassTypes;
